import { createSlice } from '@reduxjs/toolkit';
import { getDevices } from '../../utils/devicesRestAPIUtils';
import {
  getAllVendors,
  getDigitalCheckinConfigs,
  getGreGlobalConfigsByHotelId,
  getHotelRoomConfigurationDetails,
  getHotelRoomList,
  getListSpa,
  getListTemplatesData,
  getRestaurants,
  getRoomReservation,
  getSpaReservationConfigs,
  getRestaurantConfigurations,
  getTelephonyEpbx,
  getTelephonyConfigurator,
  getIntegrationConfiguration,
  getIntegrationStatusByHotelId,
  getHotelSystemSetting,
  getAppConfigurationsListApi,
} from '../../utils/IntegrationsAPIUtils';
import { notificationManager } from '../../utils/NotificationUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../utils/loaderSlice';
import { logError } from '../../utils/commonUtils';
import { ENVIRONMENT } from '../../env/Configurations';

export const configurationSlice = createSlice({
  name: 'configuration',
  initialState: {
    greGlobalDefaultTabData: {},
    greGlobalDNDTabData: {},
    greGlobalMMRTabData: {},
    greGlobalDDKData: {},
    greTemplates: [],
    vendors: [],
    greRoomList: [],
    selectedRoom: {},
    digitalCheckinConfig: {},
    roomReservation: {},
    spaList: {},
    spaReservationConfigs: {},
    restaurantList: {},
    restaurantConfigs: {},
    isTelephonyConfigsLoaded: false,
    telephonyConfigs: null,
    telephonyEpbx: [],
    integrationConfigs: [],
    integrationConfigMaps: {},
    integrationStatus: {},
    devices: [],
    hotelSystemsSetting: [],
    devicesLoader: false,
    appConfiguration: [],
  },
  reducers: {
    setGREGlobalDefaultConfigs: (state, action) => {
      state.greGlobalDefaultTabData = action.payload;
    },

    setGREGlobalDNDConfigs: (state, action) => {
      state.greGlobalDNDTabData = action.payload;
    },

    setGREGlobalMMRConfigs: (state, action) => {
      state.greGlobalMMRTabData = action.payload;
    },

    setGREGlobalDDKConfigs: (state, action) => {
      state.greGlobalDDKData = action.payload;
    },

    setGRETemplates: (state, action) => {
      state.greTemplates = action.payload;
    },

    setVendors: (state, action) => {
      state.vendors = action.payload;
    },

    setGRERoomsList: (state, action) => {
      state.greRoomList = action.payload;
    },

    setSelectedRoom: (state, action) => {
      state.selectedRoom = action.payload;
    },

    setDigitalCheckinConfigs: (state, action) => {
      state.digitalCheckinConfig = action.payload;
    },

    setSelectedRoomReservation: (state, action) => {
      state.roomReservation = action.payload;
    },

    setSpaList: (state, action) => {
      state.spaList = action.payload;
    },

    setSpaReservationConfigs: (state, action) => {
      state.spaReservationConfigs = action.payload;
    },

    setRestaurantList: (state, action) => {
      state.restaurantList = action.payload;
    },

    setRestaurantConfigs: (state, action) => {
      state.restaurantConfigs = action.payload;
    },

    setTelephonyEpbxList: (state, action) => {
      state.telephonyEpbx = action.payload;
    },

    setTelephonyConfigs: (state, action) => {
      state.telephonyConfigs = action.payload;
    },

    setTelephonyConfigsLoaded: (state, action) => {
      state.isTelephonyConfigsLoaded = action.payload;
    },
    setGREGlobalDoorCamera: (state, action) => {
      state.greGlobalDoorCamera = action.payload;
    },
    setIntegrationConfigs: (state, action) => {
      state.integrationConfigs = action.payload;
    },
    setIntegrationConfigMaps: (state, action) => {
      state.integrationConfigMaps = action.payload;
    },
    setIntegrationStatus: (state, action) => {
      state.integrationStatus = action.payload;
    },
    setDevices: (state, action) => {
      state.devices = action.payload;
    },
    setDevicesLoader: (state, action) => {
      state.devicesLoader = action.payload;
    },
    setHotelSystemsSetting: (state, action) => {
      state.hotelSystemsSetting = action.payload;
    },
    setAppConfigurationList: (state, action) => {
      state.appConfiguration = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setGREGlobalDefaultConfigs,
  setGREGlobalDNDConfigs,
  setGREGlobalMMRConfigs,
  setGREGlobalDDKConfigs,
  setGRETemplates,
  setVendors,
  setGRERoomsList,
  setSelectedRoom,
  setDigitalCheckinConfigs,
  setSelectedRoomReservation,
  setSpaList,
  setSpaReservationConfigs,
  setRestaurantList,
  setRestaurantConfigs,
  setTelephonyEpbxList,
  setTelephonyConfigs,
  setTelephonyConfigsLoaded,
  setGREGlobalDoorCamera,
  setIntegrationConfigs,
  setIntegrationConfigMaps,
  setIntegrationStatus,
  setDevices,
  setDevicesLoader,
  setHotelSystemsSetting,
  setAppConfigurationList,
} = configurationSlice.actions;

// ------------------------------------------------------------------------------------------------------------------------ //
// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(setAllGroups([{}, {}]))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched

// Fetch all GRE Global tab related details and set to global store

export const fetchAllGREGlobalConfigs = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getGreGlobalConfigsByHotelId(hotelId);
    dispatch(setGREGlobalDefaultConfigs(res.data.getGreGlobalConfigs.defaultConfigs));
    dispatch(setGREGlobalDNDConfigs(res.data.getGreGlobalConfigs.dndConfigs));
    dispatch(setGREGlobalMMRConfigs(res.data.getGreGlobalConfigs.mmrConfigs));
    dispatch(setGREGlobalDDKConfigs(res.data.getGreGlobalConfigs.ddkConfigs));
    dispatch(setGREGlobalDoorCamera(res.data.getGreGlobalConfigs.doorCameraConfigs));
  } catch (error) {
    logError(hotelId, 'fetchAllGREGlobalConfigs', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchGRETemplates = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getListTemplatesData(hotelId);
    dispatch(setGRETemplates(res.data.listTemplates));
  } catch (error) {
    logError(hotelId, 'fetchGRETemplates', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchVendors = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAllVendors(hotelId);
    dispatch(setVendors(res?.data?.GetCategorizeVendors));
  } catch (error) {
    logError(hotelId, 'fetchVendors', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchGRERoomList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getHotelRoomList(hotelId);
    dispatch(setGRERoomsList(res.data.GetRoomListByHotelId));
  } catch (error) {
    logError(hotelId, 'fetchGRERoomList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchSelectedRoom = (hotelId, roomId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getHotelRoomConfigurationDetails(hotelId, roomId);
    dispatch(setSelectedRoom(res.data.GetRoomConfigDetailsByHotelIdAndRoomId));
  } catch (error) {
    logError(hotelId, 'fetchSelectedRoom', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchDigitalCheckinConfigs = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getDigitalCheckinConfigs(hotelId);
    dispatch(setDigitalCheckinConfigs(res.data.getHotelSystemsDigitalCheckinConfig));
  } catch (error) {
    dispatch(setDigitalCheckinConfigs(null));
    logError(hotelId, 'fetchDigitalCheckinConfigs', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchROomReservationConfig = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getRoomReservation(hotelId);
    dispatch(setSelectedRoomReservation(res.data.getRoomReservations));
  } catch (error) {
    dispatch(setSelectedRoomReservation(null));
    logError(hotelId, 'fetchROomReservationConfig', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchSpaList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getListSpa(hotelId);
    dispatch(setSpaList(res?.data?.listSpa));
  } catch (error) {
    logError(hotelId, 'fetchSpaList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchSpaReservationConfigs = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getSpaReservationConfigs(hotelId);
    dispatch(setSpaReservationConfigs(res?.data?.getSpaReservationConfigs));
  } catch (error) {
    logError(hotelId, 'fetchSpaReservationConfigs', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchRestaurantList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getRestaurants(hotelId);
    dispatch(setRestaurantList(res?.data?.getRestaurantDetails));
  } catch (error) {
    logError(hotelId, 'fetchRestaurantList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchRestaurantConfigs = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getRestaurantConfigurations(hotelId);
    dispatch(setRestaurantConfigs(res?.data?.getRestaurantConfig));
  } catch (error) {
    logError(hotelId, 'fetchRestaurantConfigs', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchTelephonyEpbxList = () => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const getTelephonyEpbxList = await getTelephonyEpbx();
    dispatch(setTelephonyEpbxList(getTelephonyEpbxList?.data?.listTelephonyOptions));
  } catch (error) {
    logError(null, 'fetchTelephonyEpbxList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchTelephonyConfigurator = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const getTelephonyConfigs = await getTelephonyConfigurator(hotelId);
    dispatch(setTelephonyConfigs(getTelephonyConfigs?.data?.getTelephonyConfig));
    dispatch(setTelephonyConfigsLoaded(true));
  } catch (error) {
    dispatch(setTelephonyConfigs(null));
    dispatch(setTelephonyConfigsLoaded(true));
    logError(hotelId, 'fetchTelephonyConfigurator', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchIntegrationConfigs = (configName) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getIntegrationConfiguration(configName);
    if (configName === 'config') {
      dispatch(
        setIntegrationConfigs(JSON.parse(res.data.getIntegrationConfiguration.jsonResponse)),
      );
    }

    if (configName === 'map') {
      dispatch(
        setIntegrationConfigMaps(JSON.parse(res.data.getIntegrationConfiguration.jsonResponse)),
      );
    }
  } catch (error) {
    logError(null, 'fetchIntegrationConfigs', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchIntegrationStatus = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getIntegrationStatusByHotelId(hotelId);
    dispatch(setIntegrationStatus(res.data.getIntegrationStatusByHotelId));
  } catch (error) {
    dispatch(setIntegrationStatus(null));
    logError(hotelId, 'fetchIntegrationStatus', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchDevices = (url) => async (dispatch) => {
  dispatch(setDevicesLoader(true));
  try {
    dispatch(stepUpLoaderCount());
    const res = await getDevices(url);
    dispatch(setDevices(res.data));
  } catch (error) {
    logError(null, 'fetchDevices', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
    dispatch(setDevices([]));
  } finally {
    dispatch(setDevicesLoader(false));
    dispatch(stepDownLoaderCount());
  }
};

export const fetchHotelSystemSettings = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const getHotelSystemSettings = await getHotelSystemSetting(hotelId);
    dispatch(setHotelSystemsSetting(getHotelSystemSettings?.data?.getHotelSystemsSetting));
  } catch (error) {
    logError(hotelId, 'fetchHotelSystemSettings', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
    dispatch(setHotelSystemsSetting(null));
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAppConfigurationsList = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const getHotelSystemSettings = await getAppConfigurationsListApi(hotelId);
    if (getHotelSystemSettings?.data?.listAppConfigurations?.length) {
      dispatch(setAppConfigurationList(getHotelSystemSettings?.data?.listAppConfigurations));
    } else {
      dispatch(setAppConfigurationList([]));
    }
  } catch (error) {
    logError(hotelId, 'fetchAppConfigurationsList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// ------------------------------------------------------------------------------------------------------------------------ //

export const greGlobalDefaultTabSelector = (state) => state.configuration.greGlobalDefaultTabData;
export const greGlobalDNDTabSelector = (state) => state.configuration.greGlobalDNDTabData;
export const greGlobalMMRTabSelector = (state) => state.configuration.greGlobalMMRTabData;
export const greGlobalDDKTabSelector = (state) => state.configuration.greGlobalDDKData;
export const greTemplatesSelector = (state) => state.configuration.greTemplates;
export const vendorsSelector = (state) => state.configuration.vendors;
export const greRoomsListSelector = (state) => state.configuration.greRoomList;
export const greRoomsConfigurationDetails = (state) => state.configuration.selectedRoom;
export const digitalCheckinConfigSelector = (state) => state.configuration.digitalCheckinConfig;
export const roomReservationSelector = (state) => state.configuration.roomReservation;
export const spaSelector = (state) => state.configuration.spaList;
export const spaReservationConfigsSelector = (state) => state.configuration.spaReservationConfigs;
export const restaurantSelector = (state) => state.configuration.restaurantList.restaurant;
export const restaurantConfigsSelector = (state) => state.configuration.restaurantConfigs;
export const telephonyEpbxSelector = (state) => state.configuration.telephonyEpbx;
export const telephonyConfigsSelector = (state) => state.configuration.telephonyConfigs;
export const isTelephonyConfigsLoadedSelector = (state) =>
  state.configuration.isTelephonyConfigsLoaded;
export const greGlobalDoorCameraSelector = (state) => state.configuration.greGlobalDoorCamera;
export const integrationConfigsSelector = (state) => state.configuration.integrationConfigs;
export const integrationConfigMapsSelector = (state) => state.configuration.integrationConfigMaps;
export const integrationStatusSelector = (state) => state.configuration.integrationStatus;
export const hotelSystemSettingsSelector = (state) => state.configuration.hotelSystemsSetting;
export const devicesSelector = (state) => state.configuration.devices;
export const devicesLoaderSelector = (state) => state.configuration.devicesLoader;
export const appConfigurationSelector = (state) => state.configuration.appConfiguration;
export default configurationSlice.reducer;
